import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  Package,
  ShoppingCart,
  MessageSquareQuote,
  Users,
  Layers,
  Clock,
  ArrowRight,
  CornerDownLeft,
  ChevronUp,
  ChevronDown,
  Trash2,
  Command,
} from 'lucide-react';
import clsx from 'clsx';
import { useAdminProducts, useAdminOrders, useAdminCategories } from '../../services/adminService';
import { useAdminTeam } from '../../services/teamService';
import { getImageUrl } from '../../lib/supabase';

const RECENT_KEY = 'admin_recent_searches';
const MAX_RECENT = 8;

/**
 * Quick navigation sections the user can jump to.
 */
const QUICK_NAV = [
  { label: 'Dashboard', path: '/admin', icon: Command },
  { label: 'Products', path: '/admin/products', icon: Package },
  { label: 'Add Product', path: '/admin/products/new', icon: Package },
  { label: 'Orders', path: '/admin/orders', icon: ShoppingCart },
  { label: 'Testimonials', path: '/admin/testimonials', icon: MessageSquareQuote },
  { label: 'Team', path: '/admin/team', icon: Users },
  { label: 'Categories', path: '/admin/categories', icon: Layers },
];

function getRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function saveRecent(term) {
  const prev = getRecent().filter((t) => t !== term);
  const next = [term, ...prev].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

function clearRecent() {
  localStorage.removeItem(RECENT_KEY);
}

/**
 * Admin Search Modal — keyboard nav, recent searches, real-time results.
 */
export function AdminSearchModal({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Data sources
  const { data: products } = useAdminProducts();
  const { data: orders } = useAdminOrders();
  const { data: categories } = useAdminCategories();
  const { data: team } = useAdminTeam();

  // Load recent on open
  useEffect(() => {
    if (open) {
      setRecent(getRecent());
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Lock body
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Build results
  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase().trim();
    const items = [];

    // Pages / quick nav
    QUICK_NAV.forEach((nav) => {
      if (nav.label.toLowerCase().includes(q)) {
        items.push({ type: 'page', label: nav.label, icon: nav.icon, path: nav.path });
      }
    });

    // Products
    (products || []).forEach((p) => {
      if (
        p.title?.toLowerCase().includes(q) ||
        p.slug?.toLowerCase().includes(q) ||
        p.short_description?.toLowerCase().includes(q)
      ) {
        items.push({
          type: 'product',
          label: p.title,
          sub: p.categories?.name || 'No category',
          image: p.image_path,
          path: '/admin/products',
          id: p.id,
        });
      }
    });

    // Orders
    (orders || []).forEach((o) => {
      if (
        o.customer_name?.toLowerCase().includes(q) ||
        o.customer_phone?.toLowerCase().includes(q) ||
        o.id?.toLowerCase().includes(q)
      ) {
        items.push({
          type: 'order',
          label: o.customer_name,
          sub: `#${o.id.slice(0, 8)} · ${o.status}`,
          path: '/admin/orders',
          id: o.id,
        });
      }
    });

    // Team
    (team || []).forEach((m) => {
      if (
        m.full_name?.toLowerCase().includes(q) ||
        m.role?.toLowerCase().includes(q)
      ) {
        items.push({
          type: 'team',
          label: m.full_name,
          sub: m.role,
          image: m.image_path,
          path: '/admin/team',
          id: m.id,
        });
      }
    });

    // Categories
    (categories || []).forEach((c) => {
      if (c.name?.toLowerCase().includes(q)) {
        items.push({
          type: 'category',
          label: c.name,
          sub: 'Category',
          path: '/admin/categories',
          id: c.id,
        });
      }
    });

    return items.slice(0, 20);
  }, [query, products, orders, team, categories]);

  // Combined items: when no query, show recent + quick nav; otherwise show results
  const displayItems = useMemo(() => {
    if (query.trim()) return results;
    const items = [];
    if (recent.length > 0) {
      recent.forEach((term) => {
        items.push({ type: 'recent', label: term });
      });
    }
    return items;
  }, [query, results, recent]);

  // Clamp active index
  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const handleSelect = useCallback((item) => {
    if (item.type === 'recent') {
      setQuery(item.label);
      return;
    }
    if (item.label) saveRecent(item.label);
    setRecent(getRecent());
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  const handleKeyDown = useCallback((e) => {
    const total = displayItems.length || 0;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % Math.max(total, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + Math.max(total, 1)) % Math.max(total, 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (displayItems[activeIdx]) handleSelect(displayItems[activeIdx]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [displayItems, activeIdx, handleSelect, onClose]);

  const handleClearRecent = () => {
    clearRecent();
    setRecent([]);
  };

  const typeIcons = {
    product: Package,
    order: ShoppingCart,
    team: Users,
    category: Layers,
    page: Command,
    recent: Clock,
  };

  const typeColors = {
    product: 'bg-blue-50 text-blue-600',
    order: 'bg-green-50 text-green-600',
    team: 'bg-purple-50 text-purple-600',
    category: 'bg-amber-50 text-amber-600',
    page: 'bg-brand-light text-brand-dark',
    recent: 'bg-brand-light text-brand-gray',
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[70] bg-brand-dark/50 backdrop-blur-sm"
          />

          {/* Modal — centered */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <motion.div
              initial={{ y: -20, scale: 0.98 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: -20, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-xl max-h-[85vh] bg-brand-white rounded-2xl shadow-2xl overflow-hidden border border-ui-border flex flex-col"
            >
            {/* Search input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-ui-border flex-shrink-0">
              <Search size={18} className="text-ui-muted flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search products, orders, team, pages..."
                className="flex-1 text-sm bg-transparent border-0 outline-none placeholder:text-ui-muted text-brand-dark"
                autoComplete="off"
                spellCheck={false}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="p-1 rounded-md text-ui-muted hover:text-brand-dark hover:bg-brand-light transition-colors"
                >
                  <X size={14} />
                </button>
              )}
              <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono font-bold text-ui-muted bg-brand-light border border-ui-border rounded">
                ESC
              </kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto">
              {/* No query — recent searches */}
              {!query.trim() && recent.length > 0 && (
                <div>
                  <div className="flex items-center justify-between px-5 pt-3 pb-1">
                    <span className="text-[10px] font-bold text-brand-gray uppercase tracking-wider">Recent</span>
                    <button
                      onClick={handleClearRecent}
                      className="flex items-center gap-1 text-[10px] font-bold text-brand-gray hover:text-error transition-colors"
                    >
                      <Trash2 size={10} /> Clear
                    </button>
                  </div>
                  {displayItems.map((item, i) => (
                    <ResultRow
                      key={`recent-${i}`}
                      item={item}
                      idx={i}
                      active={i === activeIdx}
                      typeIcons={typeIcons}
                      typeColors={typeColors}
                      onSelect={handleSelect}
                      onHover={setActiveIdx}
                    />
                  ))}
                </div>
              )}

              {/* No query, no recent */}
              {!query.trim() && recent.length === 0 && (
                <div className="px-5 py-8 text-center">
                  <Search size={32} className="text-ui-muted mx-auto mb-2" />
                  <p className="text-sm font-bold text-brand-dark">Search anything</p>
                  <p className="text-xs text-brand-gray mt-1">Products, orders, team members, pages...</p>
                </div>
              )}

              {/* Query with results */}
              {query.trim() && results.length > 0 && (
                <div>
                  <div className="px-5 pt-3 pb-1">
                    <span className="text-[10px] font-bold text-brand-gray uppercase tracking-wider">
                      Results ({results.length})
                    </span>
                  </div>
                  {results.map((item, i) => (
                    <ResultRow
                      key={`${item.type}-${item.id || item.label}-${i}`}
                      item={item}
                      idx={i}
                      active={i === activeIdx}
                      typeIcons={typeIcons}
                      typeColors={typeColors}
                      onSelect={handleSelect}
                      onHover={setActiveIdx}
                    />
                  ))}
                </div>
              )}

              {/* Query with no results */}
              {query.trim() && results.length === 0 && (
                <div className="px-5 py-8 text-center">
                  <Search size={32} className="text-ui-muted mx-auto mb-2" />
                  <p className="text-sm font-bold text-brand-dark">No results found</p>
                  <p className="text-xs text-brand-gray mt-1">Try a different search term</p>
                </div>
              )}
            </div>

            {/* Footer — keyboard hints */}
            <div className="flex items-center gap-4 px-5 py-2.5 border-t border-ui-border bg-brand-light/50">
              <span className="flex items-center gap-1 text-[10px] text-brand-gray">
                <kbd className="inline-flex items-center justify-center w-4 h-4 bg-brand-white border border-ui-border rounded text-[9px] font-mono"><ChevronUp size={8} /></kbd>
                <kbd className="inline-flex items-center justify-center w-4 h-4 bg-brand-white border border-ui-border rounded text-[9px] font-mono"><ChevronDown size={8} /></kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1 text-[10px] text-brand-gray">
                <kbd className="inline-flex items-center justify-center w-4 h-4 bg-brand-white border border-ui-border rounded text-[9px] font-mono"><CornerDownLeft size={8} /></kbd>
                Select
              </span>
              <span className="flex items-center gap-1 text-[10px] text-brand-gray">
                <kbd className="inline-flex items-center px-1 h-4 bg-brand-white border border-ui-border rounded text-[9px] font-mono">ESC</kbd>
                Close
              </span>
            </div>            </motion.div>          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ── Single result row ── */
function ResultRow({ item, idx, active, typeIcons, typeColors, onSelect, onHover }) {
  const Icon = typeIcons[item.type] || Search;
  const color = typeColors[item.type] || 'bg-brand-light text-brand-gray';
  const imageUrl = item.image ? getImageUrl(item.image) : null;

  return (
    <button
      data-idx={idx}
      onClick={() => onSelect(item)}
      onMouseEnter={() => onHover(idx)}
      className={clsx(
        'flex items-center gap-3 w-full px-5 py-2.5 text-left transition-colors',
        active ? 'bg-brand-red/5' : 'hover:bg-brand-light/50'
      )}
    >
      {/* Icon or image */}
      {imageUrl ? (
        <div className="w-9 h-9 rounded-xl overflow-hidden bg-brand-light flex-shrink-0">
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0', color)}>
          <Icon size={16} />
        </div>
      )}

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className={clsx('text-sm font-medium truncate', active ? 'text-brand-dark' : 'text-brand-dark')}>
          {item.label}
        </p>
        {item.sub && (
          <p className="text-[11px] text-brand-gray truncate">{item.sub}</p>
        )}
      </div>

      {/* Type badge + arrow */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={clsx('text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-md', color)}>
          {item.type}
        </span>
        {active && <ArrowRight size={12} className="text-brand-red" />}
      </div>
    </button>
  );
}
